import type { ZodTypeAny, z } from "zod";
import { applyRepositoryScopeToUrl } from "./repository-scope";
import { supabase } from "./supabase";

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export interface ApiRequestOptions {
  query?: Record<
    string,
    string | number | boolean | ReadonlyArray<string | number | boolean> | undefined | null
  >;
  body?: unknown;
  signal?: AbortSignal;
}

export async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildUrl(path: string, query?: ApiRequestOptions["query"]): string {
  const url = new URL(path.startsWith("http") ? path : `${baseUrl}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, String(item));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return applyRepositoryScopeToUrl(url).toString();
}

async function parseError(response: Response): Promise<ApiError> {
  const text = await response.text();
  let payload: unknown = text;
  try {
    payload = JSON.parse(text);
  } catch {
    // keep raw text
  }
  const message = extractMessage(payload) ?? `Request failed with status ${response.status}`;
  return new ApiError(response.status, message, payload);
}

/** One entry of FastAPI's request-validation `detail` list. */
export interface ValidationEntry {
  /** Field path after the request part, e.g. `metadata` for `["body","metadata"]`. */
  field: string;
  /** Pydantic error type, e.g. `string_too_long`. */
  type: string;
  msg: string;
}

/**
 * Read the first entry of a FastAPI 422 body (`{"detail": [{loc, msg, type}]}`).
 * Callers map `field` + `type` to a domain-specific sentence; `extractMessage`
 * uses it for the generic fallback.
 */
export function firstValidationEntry(payload: unknown): ValidationEntry | null {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) return null;
  const detail = (payload as { detail: unknown }).detail;
  if (!Array.isArray(detail)) return null;
  const entry: unknown = detail[0];
  if (!entry || typeof entry !== "object") return null;
  const msg = (entry as { msg?: unknown }).msg;
  if (typeof msg !== "string" || msg.length === 0) return null;
  const rawType = (entry as { type?: unknown }).type;
  const loc = (entry as { loc?: unknown }).loc;
  // `loc[0]` is the request part ("body" / "query" / "path"); only the
  // field path after it means anything to the operator.
  const field = Array.isArray(loc)
    ? loc
        .slice(1)
        .filter(
          (part): part is string | number => typeof part === "string" || typeof part === "number",
        )
        .join(".")
    : "";
  return { field, type: typeof rawType === "string" ? rawType : "", msg };
}

/**
 * Read `detail.code` out of an already-parsed JSON error body shaped like
 * `{ detail: { code, ... } }` — the backend's machine-readable discriminator
 * for conditions such as `repository_archived` on a write against an
 * archived knowledge repository. Returns `null` when the shape doesn't
 * match, so callers fall back to their own handling.
 *
 * Exported (not just used by `apiErrorCode` below) so a caller working on a
 * raw `fetch` response body that never goes through `apiClient` — e.g.
 * `upload.ts`, which posts via `FormData` — matches on the exact same path
 * instead of re-deriving its own check.
 */
export function payloadDetailCode(payload: unknown): string | null {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) return null;
  const detail = (payload as { detail: unknown }).detail;
  if (!detail || typeof detail !== "object") return null;
  const code = "code" in detail ? (detail as { code: unknown }).code : null;
  return typeof code === "string" ? code : null;
}

/**
 * Extract `detail.code` from an `ApiError`'s parsed payload (see
 * `payloadDetailCode`). Returns `null` for anything that isn't an
 * `ApiError`, so callers can discriminate a specific backend error code
 * (e.g. `apiErrorCode(error) === "repository_archived"`) without matching on
 * `error.message`, which is a derived display string, not a stable contract.
 */
export function apiErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  return payloadDetailCode(error.payload);
}

function extractMessage(payload: unknown): string | null {
  if (typeof payload === "string" && payload.length > 0) return payload;
  if (payload && typeof payload === "object" && "detail" in payload) {
    const detail = (payload as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    // A validation `detail` is a LIST, so it has to be handled before the
    // object branch below — an array is `typeof "object"` and would fall
    // through to a fruitless `message` / `code` lookup.
    if (Array.isArray(detail)) {
      const entry = firstValidationEntry(payload);
      if (entry) return entry.field ? `${entry.field}: ${entry.msg}` : entry.msg;
      return null;
    }
    if (detail && typeof detail === "object") {
      // Prefer the human-readable message when the backend sends one
      // (the authentication error contract pairs `code` with `message`);
      // fall back to the bare code for the endpoints that only send that.
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message.length > 0) return message;
      const code = (detail as { code?: unknown }).code;
      if (typeof code === "string") return code;
    }
  }
  return null;
}

async function request<TSchema extends ZodTypeAny>(
  method: string,
  path: string,
  schema: TSchema,
  options: ApiRequestOptions = {},
): Promise<z.infer<TSchema>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(await authHeader()),
  };
  const init: RequestInit = { method, headers };
  if (options.signal) init.signal = options.signal;
  if (options.body instanceof FormData) {
    // Multipart: the browser writes the content type with its boundary.
    init.body = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  const response = await fetch(buildUrl(path, options.query), init);
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return schema.parse(undefined);
  const json: unknown = await response.json();
  return schema.parse(json);
}

export interface BlobResponse {
  blob: Blob;
  contentDisposition: string | null;
  checksumSha256: string | null;
}

async function requestBlob(path: string, options: Omit<ApiRequestOptions, "body"> = {}) {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(await authHeader()),
  };
  const init: RequestInit = { method: "GET", headers };
  if (options.signal) init.signal = options.signal;
  const response = await fetch(buildUrl(path, options.query), init);
  if (!response.ok) throw await parseError(response);
  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get("Content-Disposition"),
    checksumSha256: response.headers.get("X-Content-SHA256"),
  } satisfies BlobResponse;
}

export const apiClient = {
  get: <TSchema extends ZodTypeAny>(
    path: string,
    schema: TSchema,
    options?: Omit<ApiRequestOptions, "body">,
  ) => request("GET", path, schema, options),
  put: <TSchema extends ZodTypeAny>(
    path: string,
    schema: TSchema,
    body: unknown,
    options?: Omit<ApiRequestOptions, "body">,
  ) => request("PUT", path, schema, { ...options, body }),
  post: <TSchema extends ZodTypeAny>(
    path: string,
    schema: TSchema,
    body: unknown,
    options?: Omit<ApiRequestOptions, "body">,
  ) => request("POST", path, schema, { ...options, body }),
  patch: <TSchema extends ZodTypeAny>(
    path: string,
    schema: TSchema,
    body: unknown,
    options?: Omit<ApiRequestOptions, "body">,
  ) => request("PATCH", path, schema, { ...options, body }),
  delete: <TSchema extends ZodTypeAny>(
    path: string,
    schema: TSchema,
    options?: Omit<ApiRequestOptions, "body">,
  ) => request("DELETE", path, schema, options),
  getBlob: (path: string, options?: Omit<ApiRequestOptions, "body">) => requestBlob(path, options),
};
